import Paper from '@material-ui/core/Paper'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import Pagination from '@material-ui/core/TablePagination'
import TableRow from '@material-ui/core/TableRow'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import LoadingSpinner from '@/components/LoadingSpinner'
import { useGetAuctionDeals } from '@/domains/auctions/hooks/auctionDetails/getAuctionDeals'
import { AuctionElement } from '@/schemaTypes'
import Element from './Element'

export interface AuctionElementsViewerProps {
  isEditable: boolean
  auctionElements: AuctionElement[]
  auctionDate: Date
  deleteAuctionElement?: (dealId: string) => void
}

const AuctionElementsViewer: React.FC<AuctionElementsViewerProps> = ({
  isEditable,
  auctionElements,
  deleteAuctionElement,
  auctionDate,
}) => {
  const { t } = useTranslation()
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(20)

  const paginatedElements = React.useMemo(
    () =>
      auctionElements.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [auctionElements, page, rowsPerPage],
  )

  const { deals, queryResult } = useGetAuctionDeals({
    variables: {
      opts: {
        filter: {
          ids: paginatedElements.map((element) => element.dealId),
        },
      },
      auctionDate,
    },
    skip: !paginatedElements.length,
  })

  const dealsById = React.useMemo(
    () => new Map(deals.map((deal) => [deal._id, deal])),
    [deals],
  )

  React.useEffect(() => {
    const maxPage = Math.max(
      Math.ceil(auctionElements.length / rowsPerPage) - 1,
      0,
    )
    if (page > maxPage) {
      setPage(maxPage)
    }
  }, [auctionElements.length, page, rowsPerPage])

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  return (
    <TableContainer component={Paper}>
      <Table aria-label="collapsible table">
        <TableHead>
          <TableRow style={{ backgroundColor: '#e9e2f8' }}>
            <TableCell />
            <TableCell>{t('deal.booking_number')}</TableCell>
            <TableCell align="center">{t('shop.label')}</TableCell>
            {!isEditable && <TableCell align="left">{t('buyback')}</TableCell>}
            {!isEditable && (
              <TableCell align="left">{t('repurchase_fees')}</TableCell>
            )}
            <TableCell align="center">{t('payout.payout_amount')}</TableCell>
            <TableCell align="center">{t('fees.label')}</TableCell>
            <TableCell align="center">{t('payout_and_fees')}</TableCell>
            <TableCell align="center">{t('liquidation_fee')}</TableCell>
            <TableCell align="center">{t('cashy_price')}</TableCell>
            <TableCell align="center">{t('deal_latest_event')}</TableCell>
            <TableCell align="center">{t('internal_sale_data')}</TableCell>
            {isEditable && <TableCell align="center" />}
          </TableRow>
        </TableHead>
        <TableBody>
          {queryResult.loading && (
            <TableRow>
              <TableCell colSpan={isEditable ? 13 : 12} align="center">
                <LoadingSpinner />
              </TableCell>
            </TableRow>
          )}
          {paginatedElements.map((element) => {
            const deal = dealsById.get(element.dealId)
            if (!deal) {
              return (
                <TableRow key={element.dealId}>
                  <TableCell colSpan={isEditable ? 13 : 12} align="center">
                    {queryResult.loading ? (
                      <LoadingSpinner />
                    ) : (
                      t('not_available')
                    )}
                  </TableCell>
                </TableRow>
              )
            }

            return (
              <Element
                key={element.dealId}
                deal={deal}
                isEditable={isEditable}
                auctionElement={element}
                onDelete={deleteAuctionElement}
              />
            )
          })}
        </TableBody>
      </Table>

      <Pagination
        count={auctionElements.length}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </TableContainer>
  )
}

export default AuctionElementsViewer
